import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase.js';
import { injectCanvasEnhancements } from '../../lib/askAI.js';
import { renderMarketingPage } from './shared.js';

// PUBLIC callable (no sign-in needed): the admin-curated set of real
// user-created content featured in the sample gallery — see
// functions/marketplace.js getLandingShowcase. Fetched lazily after first
// paint so the landing page never waits on it; until (and unless) it
// arrives, the six built-in animation cards stand alone, exactly as before.
const getLandingShowcaseFn = httpsCallable(functions, 'getLandingShowcase');

// ─────────────────────────────────────────────────────────────────────────
// Floating background bubbles — thin, monochrome (currentColor) science
// motifs from a spread of fields (physics, chemistry, biology, astronomy,
// math) that drift slowly upward through the emptier stretches of the
// landing page, like bubbles rising through still air: a gentle multi-point
// wobble left/right rather than a straight line, a light rotate, a soft
// scale breathe, and a fade in/out at the bottom/top of the run so nothing
// pops in or out abruptly. Every bubble below is individually tuned (own
// size, speed, drift width, spin, and peak opacity) rather than cloned, and
// negative animation-delays start a bunch of them already mid-flight on
// first paint instead of all synchronized at zero. Purely decorative:
// aria-hidden, pointer-events:none, and capped at a low peak opacity (see
// style.css .mkt-bubble/bubble-float) so it never competes with real
// content or a11y.
// ─────────────────────────────────────────────────────────────────────────
const BUBBLE_ICONS = {
  // Physics — atom: nucleus + three tilted orbits (same idea as the brand
  // mark, redrawn flat here since it needs to sit inside a plain <svg>).
  atomOrbit: `<svg viewBox="0 0 32 32"><circle cx="16" cy="16" r="2" fill="currentColor"/><ellipse cx="16" cy="16" rx="14" ry="6" fill="none" stroke="currentColor" stroke-width="1.3"/><ellipse cx="16" cy="16" rx="14" ry="6" fill="none" stroke="currentColor" stroke-width="1.3" transform="rotate(60 16 16)"/><ellipse cx="16" cy="16" rx="14" ry="6" fill="none" stroke="currentColor" stroke-width="1.3" transform="rotate(120 16 16)"/></svg>`,
  // Chemistry — a benzene-style ring.
  hexagon: `<svg viewBox="0 0 32 32"><polygon points="16,3 27,9.5 27,22.5 16,29 5,22.5 5,9.5" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="16" cy="16" r="6" fill="none" stroke="currentColor" stroke-width="1.1"/></svg>`,
  // Chemistry — a small ball-and-stick molecule.
  molecule: `<svg viewBox="0 0 32 32"><line x1="16" y1="16" x2="6" y2="8" stroke="currentColor" stroke-width="1.3"/><line x1="16" y1="16" x2="26" y2="8" stroke="currentColor" stroke-width="1.3"/><line x1="16" y1="16" x2="16" y2="27" stroke="currentColor" stroke-width="1.3"/><circle cx="16" cy="16" r="3" fill="currentColor"/><circle cx="6" cy="8" r="2.4" fill="none" stroke="currentColor" stroke-width="1.1"/><circle cx="26" cy="8" r="2.4" fill="none" stroke="currentColor" stroke-width="1.1"/><circle cx="16" cy="27" r="2.4" fill="none" stroke="currentColor" stroke-width="1.1"/></svg>`,
  // Biology — a double helix with rungs.
  dna: `<svg viewBox="0 0 24 32"><path d="M4 2 C4 9, 20 9, 20 16 C20 23, 4 23, 4 30" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M20 2 C20 9, 4 9, 4 16 C4 23, 20 23, 20 30" fill="none" stroke="currentColor" stroke-width="1.4"/><line x1="6.5" y1="6" x2="17.5" y2="6" stroke="currentColor" stroke-width="1.1"/><line x1="4.5" y1="16" x2="19.5" y2="16" stroke="currentColor" stroke-width="1.1"/><line x1="6.5" y1="26" x2="17.5" y2="26" stroke="currentColor" stroke-width="1.1"/></svg>`,
  // Astronomy — a small body orbiting a larger one.
  orbitDot: `<svg viewBox="0 0 32 32"><circle cx="16" cy="16" r="3.4" fill="currentColor"/><ellipse cx="16" cy="16" rx="15" ry="9" fill="none" stroke="currentColor" stroke-width="1.3"/><circle cx="30" cy="16" r="1.8" fill="currentColor"/></svg>`,
  // Physics — a bolt of energy.
  bolt: `<svg viewBox="0 0 20 32"><polygon points="12,1 3,18 9,18 7,31 17,13 11,13" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>`,
};

function bubble({ icon, text, left, size, duration, delay, drift, spin, peak }) {
  const style = `--bx:${left}%;--bs:${size}px;--bd:${duration}s;--bdelay:${delay}s;--bdrift:${drift};--bspin:${spin};--bo:${peak};`;
  return `<span class="mkt-bubble" style="${style}">${icon ? BUBBLE_ICONS[icon] : text}</span>`;
}

function bubbleLayer(list, extraClass = '') {
  return `<div class="mkt-float-layer ${extraClass}" aria-hidden="true">${list.map(bubble).join('')}</div>`;
}

// Hero + sample-simulator gallery — the widest, emptiest stretch of the
// page, so it gets the fullest set.
const HERO_BUBBLES = [
  { icon: 'atomOrbit', left: 4, size: 40, duration: 26, delay: -6, drift: 1.2, spin: 0.8, peak: 0.30 },
  { text: 'π', left: 12, size: 30, duration: 19, delay: -11, drift: -1.4, spin: 1, peak: 0.32 },
  { icon: 'hexagon', left: 20, size: 34, duration: 23, delay: -3, drift: 0.9, spin: -1, peak: 0.26 },
  { text: '∑', left: 28, size: 26, duration: 17, delay: -9, drift: 1.6, spin: 0.6, peak: 0.30 },
  { icon: 'dna', left: 36, size: 30, duration: 28, delay: -14, drift: -1, spin: 0.5, peak: 0.24 },
  { text: '√', left: 44, size: 36, duration: 21, delay: -2, drift: 1.1, spin: -0.7, peak: 0.27 },
  { icon: 'orbitDot', left: 52, size: 42, duration: 30, delay: -18, drift: -1.3, spin: 1.1, peak: 0.25 },
  { text: '∞', left: 60, size: 28, duration: 18, delay: -5, drift: 1.4, spin: -1.2, peak: 0.33 },
  { icon: 'molecule', left: 68, size: 36, duration: 25, delay: -10, drift: -0.8, spin: 0.9, peak: 0.24 },
  { text: 'Ω', left: 76, size: 24, duration: 16, delay: -7, drift: 1.2, spin: 1, peak: 0.30 },
  { icon: 'bolt', left: 83, size: 26, duration: 20, delay: -13, drift: -1.5, spin: -0.9, peak: 0.27 },
  { text: 'ƒ(x)', left: 90, size: 22, duration: 24, delay: -4, drift: 1, spin: 0.5, peak: 0.28 },
  { icon: 'hexagon', left: 8, size: 20, duration: 33, delay: -20, drift: 0.6, spin: -0.5, peak: 0.20 },
  { text: 'π', left: 95, size: 18, duration: 29, delay: -16, drift: -1.1, spin: 0.7, peak: 0.22 },
];

// CTA band — solid accent background, so these render in white instead
// (see .mkt-float-layer-light), a smaller/quieter set to close the page.
const CTA_BUBBLES = [
  { icon: 'atomOrbit', left: 6, size: 34, duration: 22, delay: -5, drift: 1, spin: 0.7, peak: 0.16 },
  { text: 'π', left: 16, size: 22, duration: 17, delay: -9, drift: -1.2, spin: 1, peak: 0.18 },
  { icon: 'orbitDot', left: 30, size: 30, duration: 26, delay: -14, drift: 1.3, spin: -0.8, peak: 0.13 },
  { text: '∞', left: 48, size: 20, duration: 15, delay: -3, drift: -1, spin: 0.6, peak: 0.19 },
  { icon: 'dna', left: 64, size: 26, duration: 24, delay: -11, drift: 1.1, spin: 0.5, peak: 0.12 },
  { text: '√', left: 78, size: 24, duration: 19, delay: -6, drift: -1.4, spin: -1, peak: 0.17 },
  { icon: 'molecule', left: 88, size: 28, duration: 27, delay: -17, drift: 0.8, spin: 0.9, peak: 0.13 },
  { text: 'Ω', left: 94, size: 18, duration: 20, delay: -8, drift: 1, spin: -0.6, peak: 0.15 },
];

// Mid-page sections carry more text, so these two sets are sparser and even
// fainter (peaks ~0.07–0.12) — they only ever drift through the wide margins
// beside the cards, never behind a heading or paragraph.
const AUDIENCE_BUBBLES = [
  { icon: 'dna', left: 3, size: 30, duration: 30, delay: -7, drift: 1, spin: 0.5, peak: 0.20 },
  { text: '∑', left: 10, size: 22, duration: 22, delay: -15, drift: -1.1, spin: 0.8, peak: 0.18 },
  { icon: 'orbitDot', left: 92, size: 34, duration: 28, delay: -4, drift: -1.2, spin: 0.7, peak: 0.20 },
  { text: '√', left: 97, size: 20, duration: 24, delay: -12, drift: 1.3, spin: -0.6, peak: 0.17 },
  { icon: 'hexagon', left: 50, size: 18, duration: 34, delay: -20, drift: 0.6, spin: -0.4, peak: 0.14 },
];

const FEATURE_BUBBLES = [
  { icon: 'atomOrbit', left: 4, size: 32, duration: 29, delay: -9, drift: 1.1, spin: 0.6, peak: 0.20 },
  { text: 'π', left: 9, size: 20, duration: 21, delay: -3, drift: -1.2, spin: 1, peak: 0.18 },
  { icon: 'molecule', left: 93, size: 30, duration: 27, delay: -13, drift: -1, spin: 0.8, peak: 0.20 },
  { text: 'ƒ(x)', left: 96, size: 18, duration: 25, delay: -6, drift: 1.2, spin: -0.5, peak: 0.18 },
  { icon: 'bolt', left: 48, size: 20, duration: 33, delay: -18, drift: 0.7, spin: 0.5, peak: 0.15 },
];

// Exact sine curve y = sin(x) sampled over two periods, so the drawn line
// AND the tracing dot follow the real function rather than an arbitrary
// hand-placed polyline. viewBox 0..160 in x, amplitude 34px about y=50.
const SINE = (() => {
  const pts = [];
  for (let i = 0; i <= 96; i++) {
    const x = (i / 96) * 160;
    const y = 50 - 34 * Math.sin((i / 96) * Math.PI * 4);
    pts.push([x, y]);
  }
  const poly = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const path = 'M' + pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' L');
  return { poly, path };
})();

const SIM_CARDS = [
  {
    field: 'Physics',
    title: 'Simple pendulum',
    // θ(t) = θ₀·cos(√(g/L)·t): angle traces a cosine, so the swing eases
    // to a stop at each extreme and is fastest through the bottom.
    caption: 'Change length or gravity and watch the period T = 2π√(L/g) respond.',
    visual: `
      <div class="sim-visual sim-physics">
        <div class="pendulum">
          <svg class="pendulum-arc" viewBox="0 0 160 132" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
            <path d="M26,120 A108,108 0 0,1 134,120" class="arc-guide" />
          </svg>
          <div class="pendulum-arm ghost g2"><span class="pendulum-bob"></span></div>
          <div class="pendulum-arm ghost g1"><span class="pendulum-bob"></span></div>
          <div class="pendulum-arm"><span class="pendulum-bob"></span></div>
          <span class="pendulum-pivot"></span>
        </div>
      </div>`,
  },
  {
    field: 'Chemistry',
    title: 'Molecular geometry',
    // Real water molecule: bent geometry, H–O–H bond angle ≈ 104.5°,
    // gently flexing through its bending vibrational mode.
    caption: 'Build a molecule and read its true bond angles — water bends at 104.5°.',
    visual: `
      <div class="sim-visual sim-chemistry">
        <div class="h2o">
          <span class="bond bond-l"></span>
          <span class="bond bond-r"></span>
          <span class="atom-o"><b>O</b></span>
          <span class="atom-h atom-h-l"><b>H</b></span>
          <span class="atom-h atom-h-r"><b>H</b></span>
          <span class="angle-arc"></span>
          <span class="angle-label">104.5°</span>
        </div>
      </div>`,
  },
  {
    field: 'Biology',
    title: 'Mitosis',
    // Chromosomes condense, line up on the metaphase plate, then sister
    // chromatids are pulled to opposite poles before the cell pinches in two.
    caption: 'Watch chromosomes align and separate through each phase of cell division.',
    visual: `
      <div class="sim-visual sim-biology">
        <div class="mitosis">
          <span class="spindle spindle-l"></span>
          <span class="spindle spindle-r"></span>
          <span class="membrane"></span>
          <span class="chromo c1"></span>
          <span class="chromo c2"></span>
          <span class="chromo c3"></span>
          <span class="chromo c4"></span>
        </div>
      </div>`,
  },
  {
    field: 'Mathematics',
    title: 'Function grapher',
    caption: 'Adjust amplitude and frequency and trace y = sin(x) as it redraws.',
    visual: `
      <div class="sim-visual sim-math">
        <svg viewBox="0 0 160 100" class="graph-svg" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <g class="graph-grid">
            <line x1="0" y1="16" x2="160" y2="16" /><line x1="0" y1="50" x2="160" y2="50" class="axis" />
            <line x1="0" y1="84" x2="160" y2="84" />
            <line x1="40" y1="6" x2="40" y2="94" /><line x1="80" y1="6" x2="80" y2="94" class="axis" />
            <line x1="120" y1="6" x2="120" y2="94" />
          </g>
          <polyline points="${SINE.poly}" class="graph-line" pathLength="100" />
          <circle r="3.6" class="graph-dot">
            <animateMotion dur="4s" repeatCount="indefinite" calcMode="linear" path="${SINE.path}" />
          </circle>
        </svg>
      </div>`,
  },
  {
    field: 'Astronomy',
    title: 'Kepler orbit',
    // Elliptical orbit with the star at one focus; the planet sweeps equal
    // areas in equal times, so it speeds up at perihelion and slows at
    // aphelion (keyTimes below stretch time over the far arc).
    caption: "Kepler's laws in motion — the planet races at perihelion, drifts at aphelion.",
    visual: `
      <div class="sim-visual sim-astronomy">
        <svg viewBox="0 0 160 100" class="orbit-svg" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <ellipse cx="72" cy="50" rx="60" ry="36" class="orbit-path" />
          <circle cx="122" cy="50" r="9" class="star" />
          <circle cx="122" cy="50" r="9" class="star-core" />
          <circle r="4.4" class="planet">
            <animateMotion dur="7s" repeatCount="indefinite" calcMode="linear" rotate="auto"
              keyPoints="0;0.25;0.5;0.75;1" keyTimes="0;0.17;0.5;0.83;1"
              path="M132,50 A60,36 0 1,1 12,50 A60,36 0 1,1 132,50" />
          </circle>
        </svg>
      </div>`,
  },
  {
    field: 'Electronics',
    title: 'Circuit lab',
    // A closed loop with a cell (long line = +, short = −) driving current
    // through a resistor; conventional current animates from + around to −,
    // and the lamp brightens with the flow.
    caption: 'Wire a battery, resistor and lamp — see conventional current flow and Ohm’s law.',
    visual: `
      <div class="sim-visual sim-circuit">
        <svg viewBox="0 0 160 100" class="circuit-svg" aria-hidden="true">
          <path d="M20 78 H140 V22 H20 Z" class="circuit-wire" />
          <path id="loop" d="M20 78 H140 V22 H20 Z" class="circuit-flow" pathLength="100" />
          <!-- battery cell on the left rail -->
          <line x1="20" y1="44" x2="20" y2="52" class="cell-long" />
          <line x1="14" y1="46" x2="26" y2="46" class="cell-long-plate" />
          <line x1="16" y1="56" x2="24" y2="56" class="cell-short-plate" />
          <line x1="20" y1="56" x2="20" y2="60" class="cell-long" />
          <!-- resistor (zig-zag) on the top rail -->
          <polyline points="58,22 63,14 71,30 79,14 87,30 95,14 102,22" class="resistor" />
          <!-- lamp on the right rail -->
          <circle cx="140" cy="50" r="8" class="lamp-glow" />
          <circle cx="140" cy="50" r="6" class="lamp" />
          <path d="M136 46 L144 54 M144 46 L136 54" class="lamp-filament" />
          <!-- electrons riding the loop -->
          <circle r="2.4" class="charge"><animateMotion dur="3.2s" repeatCount="indefinite"><mpath href="#loop"/></animateMotion></circle>
          <circle r="2.4" class="charge"><animateMotion dur="3.2s" begin="-1.07s" repeatCount="indefinite"><mpath href="#loop"/></animateMotion></circle>
          <circle r="2.4" class="charge"><animateMotion dur="3.2s" begin="-2.13s" repeatCount="indefinite"><mpath href="#loop"/></animateMotion></circle>
        </svg>
      </div>`,
  },
];

const AUDIENCE = [
  {
    title: 'For school students',
    points: [
      'Turn tricky homework topics into something you can poke at and see',
      'Bright, game-like visuals that make a concept click instantly',
      'No coding needed — just describe what you want to explore',
    ],
  },
  {
    title: 'For college & self-learners',
    points: [
      'Go beyond the textbook with adjustable parameters and real controls',
      'Build lab-style simulations for circuits, mechanics, data, and more',
      'Save, revisit, and refine your work as your understanding deepens',
    ],
  },
];

const FEATURES = [
  { icon: '🛠️', title: 'Learn by building', text: 'Every topic becomes something interactive — a model, a simulator, or a quiz — instead of a static page.' },
  { icon: '🎛️', title: 'Real controls', text: 'Sliders, toggles, and inputs that actually change the simulation, not decoration.' },
  { icon: '⚡', title: 'See it instantly', text: 'Describe an idea and watch it turn into a working, visual lesson right away.' },
  { icon: '📚', title: 'Every subject', text: 'Physics, chemistry, biology, math, astronomy, economics, history timelines — if it can be modeled, it can be built here.' },
];

export function renderLanding(container, signedIn) {
  const body = `
    <main class="mkt-main">
      <div class="mkt-hero-zone">
        ${bubbleLayer(HERO_BUBBLES)}
        <section class="mkt-hero">
          <div class="mkt-hero-badges">
            <span class="pill">For school students</span>
            <span class="pill">For college & self-learners</span>
          </div>
          <h1>Turn any subject into something you can build, run, and break.</h1>
          <p class="mkt-hero-sub">
            Simustage turns your curiosity into interactive simulations, working models,
            and quizzes — made for the way you actually learn, whether that's your first
            physics lab or your final-year project.
          </p>
          <div class="mkt-hero-actions">
            <a href="#/login" class="btn btn-primary btn-lg">Get started free</a>
            <a href="#sim-gallery" class="btn btn-lg">See sample simulations</a>
          </div>
        </section>

        <section class="mkt-section mkt-section-wide" id="sim-gallery">
          <div class="mkt-section-head">
            <h2>Explore simulations across every field</h2>
            <p>A few examples of what learners have built. Yours can look however you imagine it.</p>
          </div>
          <div class="sim-carousel">
            <button type="button" class="sim-carousel-arrow sim-carousel-prev" id="sim-prev" aria-label="Previous samples" hidden>‹</button>
            <div class="sim-grid" id="sim-grid"></div>
            <button type="button" class="sim-carousel-arrow sim-carousel-next" id="sim-next" aria-label="More samples" hidden>›</button>
          </div>
          <div class="sim-carousel-dots" id="sim-dots" aria-hidden="true"></div>
        </section>
      </div>

      <section class="mkt-section mkt-section-alt mkt-float-section">
        ${bubbleLayer(AUDIENCE_BUBBLES)}
        <div class="mkt-section-head">
          <h2>Built for every stage of learning</h2>
        </div>
        <div class="audience-grid">
          ${AUDIENCE.map((a) => `
            <div class="audience-card">
              <h3>${a.title}</h3>
              <ul>${a.points.map((p) => `<li>${p}</li>`).join('')}</ul>
            </div>
          `).join('')}
        </div>
      </section>

      <section class="mkt-section mkt-float-section">
        ${bubbleLayer(FEATURE_BUBBLES)}
        <div class="mkt-section-head">
          <h2>Why learners choose Simustage</h2>
        </div>
        <div class="feature-grid">
          ${FEATURES.map((f) => `
            <div class="feature-card">
              <div class="feature-icon">${f.icon}</div>
              <h3>${f.title}</h3>
              <p>${f.text}</p>
            </div>
          `).join('')}
        </div>
      </section>

      <section class="mkt-section mkt-section-alt">
        <div class="mkt-section-head">
          <h2>Bringing a whole school or college?</h2>
          <p>One prepaid seat licence covers all your students — no per-student billing, no payment
          details from families. You buy seats at a wholesale price, set your own price for your
          community, and manage everything from your own school dashboard. Teachers create for free
          every month and never use a student seat.</p>
        </div>
        <div style="text-align:center;">
          <a href="#/schools" class="btn btn-primary btn-lg">Simustage for schools</a>
        </div>
      </section>

      <section class="mkt-cta-band">
        ${bubbleLayer(CTA_BUBBLES, 'mkt-float-layer-light')}
        <div class="mkt-cta-inner">
          <h2>Ready to build your first simulation?</h2>
          <p>Free to start. No credit card required.</p>
          <a href="#/login" class="btn btn-primary btn-lg">Get started free</a>
        </div>
      </section>
    </main>
  `;
  renderMarketingPage(container, '/', signedIn, body);
  attachSimGallery(container);
}

// ─────────────────────────────────────────────────────────────────────────
// Sample gallery carousel — SIX CARDS PER BATCH (as before), now paged with
// left/right arrows. The first batch is the six built-in animation cards;
// further batches are REAL CREATED CONTENT (admin-featured marketplace
// listings, fetched from the public getLandingShowcase callable) rendered
// live inside sandbox="allow-scripts" iframes — the same isolation used for
// every other content embed in the app. Iframes are only instantiated when
// their page is first shown (and torn down when paged away) so the landing
// page never runs a dozen simulations at once.
// ─────────────────────────────────────────────────────────────────────────
const GALLERY_PAGE_SIZE = 6;

function escText(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function attachSimGallery(container) {
  const grid = container.querySelector('#sim-grid');
  const prevBtn = container.querySelector('#sim-prev');
  const nextBtn = container.querySelector('#sim-next');
  const dotsEl = container.querySelector('#sim-dots');
  if (!grid) return;

  // Card models: built-ins render their handcrafted visual; created ones get
  // a live iframe mounted after the grid HTML lands.
  const cards = SIM_CARDS.map((c) => ({ kind: 'builtin', ...c }));
  let page = 0;

  function pageCount() {
    return Math.max(1, Math.ceil(cards.length / GALLERY_PAGE_SIZE));
  }

  function renderPage() {
    const start = page * GALLERY_PAGE_SIZE;
    const slice = cards.slice(start, start + GALLERY_PAGE_SIZE);
    grid.innerHTML = slice.map((c) => c.kind === 'builtin' ? `
      <div class="sim-card">
        ${c.visual}
        <div class="sim-card-body">
          <span class="pill sim-field-pill">${c.field}</span>
          <h3>${c.title}</h3>
          <p>${c.caption}</p>
        </div>
      </div>` : `
      <div class="sim-card sim-card-created">
        <div class="sim-visual sim-visual-created" data-created-id="${escText(c.id)}"></div>
        <div class="sim-card-body">
          <span class="pill sim-field-pill">✨ Created on Simustage</span>
          <h3>${escText(c.title)}</h3>
          <p>${escText(c.description || 'Built by a learner with nothing but a conversation — running live right here.')}</p>
        </div>
      </div>`).join('');

    // Mount live embeds for this page only (srcdoc is set programmatically —
    // inlining arbitrary HTML into an attribute would be an escaping bug).
    slice.forEach((c) => {
      if (c.kind !== 'created') return;
      const host = grid.querySelector(`[data-created-id="${CSS.escape(c.id)}"]`);
      if (!host) return;
      const frame = document.createElement('iframe');
      frame.setAttribute('sandbox', 'allow-scripts');
      frame.setAttribute('allowfullscreen', '');
      frame.setAttribute('allow', 'fullscreen');
      frame.setAttribute('loading', 'lazy');
      frame.setAttribute('title', c.title || 'Created content sample');
      frame.style.cssText = 'width:100%;height:100%;border:none;pointer-events:auto;background:#fff;';
      frame.srcdoc = injectCanvasEnhancements(c.html);
      host.appendChild(frame);
    });

    const pages = pageCount();
    const hasPaging = pages > 1;
    prevBtn.hidden = !hasPaging;
    nextBtn.hidden = !hasPaging;
    prevBtn.disabled = page === 0;
    nextBtn.disabled = page >= pages - 1;
    dotsEl.innerHTML = hasPaging
      ? Array.from({ length: pages }, (_, i) => `<span class="sim-dot${i === page ? ' is-active' : ''}"></span>`).join('')
      : '';
  }

  prevBtn?.addEventListener('click', () => { if (page > 0) { page -= 1; renderPage(); } });
  nextBtn?.addEventListener('click', () => { if (page < pageCount() - 1) { page += 1; renderPage(); } });

  renderPage();

  // Pull in the featured created content in the background; append and
  // reveal the arrows only if anything actually comes back.
  getLandingShowcaseFn({}).then(({ data }) => {
    const items = Array.isArray(data?.items) ? data.items : [];
    if (!items.length || !grid.isConnected) return;
    items.forEach((item) => {
      if (item && item.id && item.html) cards.push({ kind: 'created', ...item });
    });
    renderPage();
  }).catch(() => { /* built-in cards already cover the section */ });
}
